/**
 * React Query hooks for SageMaker connection data fetching and mutations.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getSageMakerSetupInstructions,
  listSageMakerConnections,
  createSageMakerConnection,
  deleteSageMakerConnection,
  syncSageMakerConnection,
} from "@/services/sagemakerService";
import type { SageMakerConnectionCreate } from "@/types/sagemaker";

const QUERY_KEY = "sagemaker-connections";

export function useSageMakerConnections(page: number = 1, itemsPerPage: number = 10) {
  return useQuery({
    queryKey: [QUERY_KEY, page, itemsPerPage],
    queryFn: () => listSageMakerConnections(page, itemsPerPage),
    staleTime: 30_000,
  });
}

export function useSageMakerSetupInstructions(enabled: boolean) {
  return useQuery({
    queryKey: ["sagemaker-setup-instructions"],
    queryFn: getSageMakerSetupInstructions,
    enabled,
    staleTime: 5 * 60_000,
  });
}

export function useCreateSageMakerConnection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SageMakerConnectionCreate) => createSageMakerConnection(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY], refetchType: "all" });
    },
  });
}

export function useDeleteSageMakerConnection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (connectionId: number) => deleteSageMakerConnection(connectionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY], refetchType: "all" });
    },
  });
}

export function useSyncSageMakerConnection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (connectionId: number) => syncSageMakerConnection(connectionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY], refetchType: "all" });
    },
  });
}
